import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Search,
  Calendar,
  Clock,
  ArrowRight,
  Tag
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

export const Blog = ({ onDemoClick }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const categories = [
    'Todos',
    'EAD',
    'Produção de Cursos',
    'NR-01',
    'T&D',
    'Gestão de Pessoas',
  ];

  const [activeCategory, setActiveCategory] = useState('Todos');

  const posts = [
    {
      id: 1,
      title: 'Como implementar uma Universidade Corporativa em 2024',
      excerpt: 'Guia completo com os passos essenciais para criar e implementar uma universidade corporativa de sucesso na sua empresa.',
      image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop',
      category: 'EAD',
      date: '15 Jan 2024',
      readTime: '8 min',
      featured: true,
    },
    {
      id: 2,
      title: 'NR-01 Psicossocial: Tudo que você precisa saber',
      excerpt: 'Entenda as mudanças na NR-01 relacionadas aos riscos psicossociais e como adequar sua empresa.',
      image: 'https://images.unsplash.com/photo-1573497620053-ea5300f94f21?w=600&h=400&fit=crop',
      category: 'NR-01',
      date: '10 Jan 2024',
      readTime: '6 min',
      featured: true,
    },
    {
      id: 3,
      title: '5 tendências em produção de conteúdo educacional para 2024',
      excerpt: 'Descubra as principais tendências que estão transformando a produção de cursos corporativos.',
      image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&h=400&fit=crop',
      category: 'Produção de Cursos',
      date: '05 Jan 2024',
      readTime: '5 min',
      featured: false,
    },
    {
      id: 4,
      title: 'ROI em treinamentos: como medir e apresentar resultados',
      excerpt: 'Aprenda a calcular e demonstrar o retorno sobre investimento dos seus programas de capacitação.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop',
      category: 'T&D',
      date: '28 Dez 2023',
      readTime: '7 min',
      featured: false,
    },
    {
      id: 5,
      title: 'Gamificação no e-learning: aumentando o engajamento',
      excerpt: 'Como usar elementos de games para tornar seus treinamentos mais envolventes e eficazes.',
      image: 'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=600&h=400&fit=crop',
      category: 'EAD',
      date: '20 Dez 2023',
      readTime: '6 min',
      featured: false,
    },
    {
      id: 6,
      title: 'Saúde mental no trabalho: responsabilidades da empresa',
      excerpt: 'Conheça as obrigações legais e boas práticas para promover a saúde mental dos colaboradores.',
      image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600&h=400&fit=crop',
      category: 'Gestão de Pessoas',
      date: '15 Dez 2023',
      readTime: '8 min',
      featured: false,
    },
  ];

  const filteredPosts = posts.filter((post) => {
    const matchesCategory = activeCategory === 'Todos' || post.category === activeCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPosts = filteredPosts.filter((post) => post.featured);
  const regularPosts = filteredPosts.filter((post) => !post.featured);

  return (
    <div data-testid="blog-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-50 via-blue-50/30 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
              Blog <span className="text-blue-600">CSP</span>
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Conteúdos, tendências e novidades sobre educação corporativa, 
              produção de cursos, NR-01 e muito mais.
            </p>

            {/* Search */}
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Buscar artigos..."
                data-testid="blog-search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 py-6 rounded-full border-slate-200"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                data-testid={`blog-category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      {featuredPosts.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">Destaques</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {featuredPosts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full overflow-hidden group hover:shadow-xl transition-all border-0 shadow-md">
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4">
                        <Badge className="bg-blue-600 text-white">{post.category}</Badge>
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {post.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {post.readTime}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-slate-600 mb-4">{post.excerpt}</p>
                      <Link
                        to={`/blog/${post.id}`}
                        className="inline-flex items-center text-blue-600 font-medium hover:gap-2 transition-all"
                      >
                        Ler mais <ArrowRight className="ml-1 h-4 w-4" />
                      </Link>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Posts */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-8">Todos os Artigos</h2>
          
          {regularPosts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {regularPosts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="h-full overflow-hidden group hover:shadow-lg transition-all bg-white border-0">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <Badge variant="secondary" className="bg-white/90 text-slate-700">
                          {post.category}
                        </Badge>
                      </div>
                    </div>
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {post.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {post.readTime}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-slate-600 text-sm line-clamp-2 mb-3">{post.excerpt}</p>
                      <Link
                        to={`/blog/${post.id}`}
                        className="inline-flex items-center text-blue-600 text-sm font-medium hover:gap-2 transition-all"
                      >
                        Ler mais <ArrowRight className="ml-1 h-3 w-3" />
                      </Link>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-slate-500">Nenhum artigo encontrado.</p>
            </div>
          )}

          {/* Load More */}
          <div className="text-center mt-12">
            <Button
              variant="outline"
              data-testid="blog-load-more"
              className="px-8 py-3 rounded-full"
            >
              Carregar mais artigos
            </Button>
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Receba conteúdos exclusivos
            </h2>
            <p className="text-blue-100 mb-8">
              Assine nossa newsletter e receba as melhores dicas sobre educação corporativa.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Seu melhor e-mail"
                data-testid="newsletter-email"
                className="flex-1 py-6 rounded-full bg-white"
              />
              <Button
                data-testid="newsletter-submit"
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 rounded-full font-semibold"
              >
                Assinar
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
